import { Box, Stack } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import AuthorizedBox from "@entrilia/shared/components/AuthorizedBox";
import { useGridDataSourceWithRange } from "@entrilia/shared/components/grid/gridHooks";
import { getRowsByIds } from "@entrilia/shared/utilities/dataGridApiHelper";
import biApi from "../../../../api/biApi";
import type { WorkbookTemplate } from "../../../../api/types/workbookRuns.types";
import useClientContext from "../../../../context/useClientContext";
import { FilterContextProvider } from "../../../common/filters/FilterContextProvider";
import FiltersPanel from "../../../common/filters/FiltersPanel";
import { getInitialFilterState } from "../../../common/filters/filterState";
import WorkbookCreatorFilter from "../common/WorkbookCreatorFilter";
import DeleteTemplatesDialog from "../dialogs/delete-templates/DeleteTemplatesDialog";
import NewRunDialog from "../dialogs/new-run-dialog/NewRunDialog";
import useWorkbookCreatorCounts from "../hooks/useWorkbookCreatorCounts";
import WorkbookTemplatesGrid from "./WorkbookTemplatesGrid";
import { getWorkbookTemplatesFilterPayload } from "./workbookTemplatesGridFilters";

const WorkbookTemplatesContainer = () => {
  const navigate = useNavigate();
  const [showNewRunDialog, setShowNewRunDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WorkbookTemplate | undefined>(undefined);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [templatesToDelete, setTemplatesToDelete] = useState<WorkbookTemplate[]>();
  const [createdByMe, setCreatedByMe] = useState(false);
  const { clientCode } = useClientContext();

  const filterId = useMemo(() => `${clientCode}_workbook_templates_filter_v2`, [clientCode]);
  const [filterState, setFilterState] = useState(() => getInitialFilterState<WorkbookTemplate>(filterId, [], []));
  const filterPayload = useMemo(() => getWorkbookTemplatesFilterPayload(filterState), [filterState]);
  const gridPayload = useMemo(() => ({ ...filterPayload, createdByMe }), [filterPayload, createdByMe]);

  const { gridApiRef, dataSource, totalRows, isLoading, isRefreshing, refreshDataSource, reloadDataSource } =
    useGridDataSourceWithRange(biApi.searchWorkbookTemplates, gridPayload);
  const { counts, refreshCounts } = useWorkbookCreatorCounts(biApi.searchWorkbookTemplates, filterPayload);

  const handleNewRun = useCallback(
    (templateId: string) => {
      const template = gridApiRef.current?.getRow<WorkbookTemplate>(templateId);
      if (!template) {
        return;
      }
      setSelectedTemplate(template);
      setShowNewRunDialog(true);
    },
    [gridApiRef]
  );

  const handleRefresh = () => {
    refreshDataSource();
    refreshCounts();
  };

  const handleDeleteTemplates = useCallback(
    (ids: string[]) => {
      setTemplatesToDelete(getRowsByIds<WorkbookTemplate>(gridApiRef, ids));
    },
    [gridApiRef]
  );

  const handleTemplatesDeleted = (deletedIds: string[]) => {
    setTemplatesToDelete(undefined);
    setSelectedIds((prev) => prev.filter((id) => !deletedIds.includes(id)));
    reloadDataSource();
    refreshCounts();
  };

  const handleCloseNewRunDialog = useCallback(() => {
    setShowNewRunDialog(false);
    setSelectedTemplate(undefined);
  }, []);

  const getTemplatePath = useCallback((templateId: string) => `template/${templateId}`, []);

  const handleRunStart = (runId: string) => {
    navigate(`run/${runId}`);
  };

  return (
    <Stack spacing={2} sx={{ height: "100%" }}>
      <Box>
        <WorkbookCreatorFilter createdByMe={createdByMe} counts={counts} onChange={setCreatedByMe} />
      </Box>
      <FilterContextProvider filterState={filterState} setFilterState={setFilterState}>
        <FiltersPanel
          totalCount={totalRows}
          recordCount={totalRows ?? 0}
          isLoading={isLoading}
          isRefreshing={isRefreshing}
          onRefresh={handleRefresh}
        />
        <WorkbookTemplatesGrid
          apiRef={gridApiRef}
          dataSource={dataSource}
          filterId={filterId}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          getTemplatePath={getTemplatePath}
          onNewRun={handleNewRun}
          onDeleteTemplates={handleDeleteTemplates}
        />
      </FilterContextProvider>
      <AuthorizedBox permissions={["ManageReports"]}>
        {showNewRunDialog && (
          <NewRunDialog onClose={handleCloseNewRunDialog} onStartRun={handleRunStart} template={selectedTemplate} />
        )}
        {templatesToDelete && (
          <DeleteTemplatesDialog
            templates={templatesToDelete}
            onClose={() => setTemplatesToDelete(undefined)}
            onDeleted={handleTemplatesDeleted}
          />
        )}
      </AuthorizedBox>
    </Stack>
  );
};

export default WorkbookTemplatesContainer;
